"use client";

import React, { useState } from "react";
import { Calculator, ArrowRight, TrendingUp, Sparkles, DollarSign, BedDouble, Percent, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RangeInput } from "@/components/ui/range-input";
import { Link } from "@/components/common/Link";
export default function RevenueCalculator() {
  const [rooms, setRooms] = useState<number>(45);
  const [adr, setAdr] = useState<number>(135);
  const [occupancy, setOccupancy] = useState<number>(68);
  const safeRooms = Math.max(1, rooms || 0);
  const safeAdr = Math.max(1, adr || 0);
  const safeOccupancy = Math.min(100, Math.max(0, occupancy || 0));

  // Calculations
  const occupancyRate = safeOccupancy / 100;
  const currentRevPAR = safeAdr * occupancyRate;
  const dailyRevenue = safeRooms * currentRevPAR;
  const monthlyRevenue = dailyRevenue * 30.4167; // average month days (365/12)
  const annualRevenue = dailyRevenue * 365;

  // 15% - 25% uplift projection
  const minUpliftPercent = 0.15;
  const maxUpliftPercent = 0.25;
  const minProjectedRevPAR = currentRevPAR * (1 + minUpliftPercent);
  const maxProjectedRevPAR = currentRevPAR * (1 + maxUpliftPercent);
  const minAdditionalAnnual = annualRevenue * minUpliftPercent;
  const maxAdditionalAnnual = annualRevenue * maxUpliftPercent;
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0
    }).format(amount);
  };
  const formatCurrencyDecimals = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  return <section id="revenuecalculator" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary/15 text-secondary border border-secondary/30">
            <Sparkles className="size-3.5 mr-1" />
            Interactive Revenue Estimator
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            Calculate your revenue potential
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Adjust your property parameters below to instantly benchmark current performance and project revenue growth through systematic rate distribution and inventory optimization.
          </p>
        </div>

        {/* Main Grid: Inputs vs Outputs */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Property Inputs Panel */}
          <div className="lg:col-span-5">
            <Card className="border border-border/80 bg-card shadow-sm">
              <CardHeader className="border-b border-border pb-5">
                <div className="flex items-center gap-2 text-secondary">
                  <Calculator className="size-5" />
                  <CardTitle className="text-xl font-semibold text-card-foreground">
                    Property Parameters
                  </CardTitle>
                </div>
                <CardDescription className="text-xs text-muted-foreground mt-1">
                  Input your baseline operational metrics to run live revenue projections.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6 pt-6">
                {/* Room Inventory */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="room-inventory" className="text-sm font-medium flex items-center gap-1.5">
                      <BedDouble className="size-4 text-muted-foreground" />
                      Total room inventory
                    </Label>
                    <span className="text-xs font-mono font-semibold px-2 py-0.5 rounded bg-muted text-foreground">
                      {safeRooms} keys
                    </span>
                  </div>
                  <Input id="room-inventory" type="number" min={1} max={1500} value={rooms || ""} onChange={e => setRooms(parseInt(e.target.value, 10) || 0)} className="h-11 bg-background border-border text-foreground focus-visible:ring-secondary text-base" placeholder="e.g. 45" />
                </div>

                {/* Average Daily Rate */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="average-daily-rate" className="text-sm font-medium flex items-center gap-1.5">
                      <DollarSign className="size-4 text-muted-foreground" />
                      Average daily rate (ADR)
                    </Label>
                    <span className="text-xs font-mono font-semibold px-2 py-0.5 rounded bg-muted text-foreground">
                      {formatCurrencyDecimals(safeAdr)}
                    </span>
                  </div>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-sm">
                      $
                    </span>
                    <Input id="average-daily-rate" type="number" min={1} step="any" value={adr || ""} onChange={e => setAdr(parseFloat(e.target.value) || 0)} className="pl-8 h-11 bg-background border-border text-foreground focus-visible:ring-secondary text-base font-mono" placeholder="e.g. 135.00" />
                  </div>
                </div>

                {/* Occupancy Slider */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="occupancy-rate" className="text-sm font-medium flex items-center gap-1.5">
                      <Percent className="size-4 text-muted-foreground" />
                      Average occupancy
                    </Label>
                    <span className="text-sm font-mono font-bold text-secondary bg-secondary/10 px-2 py-0.5 rounded border border-secondary/20">
                      {safeOccupancy}%
                    </span>
                  </div>
                  <RangeInput id="occupancy-rate" min={0} max={100} step={1} value={safeOccupancy} onValueChange={val => setOccupancy(val)} className="accent-secondary h-2 bg-muted rounded-full cursor-pointer" />
                  <div className="flex justify-between text-[11px] text-muted-foreground font-mono">
                    <span>0%</span>
                    <span>25%</span>
                    <span>50%</span>
                    <span>75%</span>
                    <span>100%</span>
                  </div>
                </div>

                {/* Assumptions Note */}
                <div className="pt-2">
                  <div className="rounded-md bg-muted/60 p-3 text-xs text-muted-foreground border border-border/80 flex items-start gap-2">
                    <Info className="size-4 shrink-0 text-secondary mt-0.5" />
                    <p className="leading-relaxed">
                      <strong>Assumptions note:</strong> Calculations reflect a standard 30-day/365-day calendar distribution baseline and do not include unmodeled seasonal swings or localized blackout dates.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Outputs Column */}
          <div className="lg:col-span-7 space-y-6">
            {/* Top 2 Baseline Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Current Monthly Revenue */}
              <Card className="border border-border/80 bg-card shadow-xs">
                <CardContent className="p-6">
                  <div className="text-xs uppercase font-semibold text-muted-foreground tracking-wider mb-1">
                    Current Monthly Revenue
                  </div>
                  <div className="font-serif text-2xl sm:text-3xl font-bold text-foreground">
                    {formatCurrency(monthlyRevenue)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Estimated 30-day run rate based on {safeOccupancy}% occupancy.
                  </p>
                </CardContent>
              </Card>

              {/* Current Annual Baseline */}
              <Card className="border border-border/80 bg-card shadow-xs">
                <CardContent className="p-6">
                  <div className="text-xs uppercase font-semibold text-muted-foreground tracking-wider mb-1">
                    Current Annual Baseline
                  </div>
                  <div className="font-serif text-2xl sm:text-3xl font-bold text-foreground">
                    {formatCurrency(annualRevenue)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Full 365-day unoptimized gross room revenue estimate.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Strategic Projection Card */}
            <Card className="border-2 border-secondary/40 bg-card text-card-foreground shadow-md relative overflow-hidden">
              <div className="absolute top-0 right-0 transform translate-x-4 -translate-y-4 w-32 h-32 bg-secondary/10 rounded-full blur-2xl pointer-events-none" />

              <CardHeader className="border-b border-border pb-4 bg-muted/30">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="size-5 text-secondary" />
                    <CardTitle className="text-xl font-bold text-foreground font-serif">
                      Strategic Projection
                    </CardTitle>
                  </div>
                  <Badge variant="secondary" className="bg-secondary text-secondary-foreground font-medium text-xs">
                    +15% to +25% Uplift Band
                  </Badge>
                </div>
                <CardDescription className="text-xs text-muted-foreground mt-1">
                  Potential revenue unlock achieved through dynamic pricing, distribution parity, and OTA channel optimization.
                </CardDescription>
              </CardHeader>

              <CardContent className="p-6 space-y-6">
                {/* Metric Rows */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-muted/40 border border-border">
                    <span className="text-xs text-muted-foreground block font-medium mb-1">
                      Current RevPAR
                    </span>
                    <span className="text-xl sm:text-2xl font-bold font-mono text-foreground">
                      {formatCurrencyDecimals(currentRevPAR)}
                    </span>
                    <span className="text-[11px] text-muted-foreground block mt-0.5">
                      (ADR ${safeAdr} × {safeOccupancy}% Occ)
                    </span>
                  </div>

                  <div className="p-4 rounded-lg bg-secondary/10 border border-secondary/30">
                    <span className="text-xs text-foreground/80 block font-medium mb-1">
                      Projected RevPAR Range
                    </span>
                    <span className="text-xl sm:text-2xl font-bold font-mono text-secondary">
                      {formatCurrencyDecimals(minProjectedRevPAR)} – {formatCurrencyDecimals(maxProjectedRevPAR)}
                    </span>
                    <span className="text-[11px] text-muted-foreground block mt-0.5">
                      Target with active yield management
                    </span>
                  </div>
                </div>

                {/* Highlight Additional Annual Revenue */}
                <div className="rounded-xl p-5 bg-gradient-to-r from-primary to-primary/90 text-primary-foreground">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-secondary flex items-center gap-1.5 mb-1">
                        <Sparkles className="size-3.5" />
                        Estimated Additional Annual Revenue
                      </span>
                      <div className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-primary-foreground tracking-tight">
                        {formatCurrency(minAdditionalAnnual)} – {formatCurrency(maxAdditionalAnnual)}
                      </div>
                    </div>
                    <div className="text-xs text-primary-foreground/80 sm:text-right max-w-xs leading-relaxed">
                      Expected gross annual gain from tailored channel management &amp; strategic rate positioning.
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Closing CTA Strip */}
        <div className="mt-12 md:mt-16 rounded-xl border border-border bg-card p-6 sm:p-8 shadow-xs">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
            <div className="space-y-1">
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground">
                Ready to audit your real rate potential?
              </h3>
              <p className="text-sm text-muted-foreground max-w-2xl">
                Get a comprehensive revenue evaluation and custom OTA distribution plan tailored to your property.
              </p>
            </div>

            <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-semibold shadow-xs shrink-0">
              <Link to="#" className="inline-flex items-center gap-2">
                Book a Consultation
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}